import React, { useEffect, useMemo, useState } from 'react';
import {
    Card, Container, Group, SimpleGrid, Text, Title, TextInput,
} from '@mantine/core';
import ReactECharts from 'echarts-for-react';
import client from '../API/API';
import LoaderComponent from '../components/loader/LoaderComponent';
import NoDataAvailable from '../components/noDataAvailable/NoDataAvailable';

const PALETTE = [
    '#1F4E79',
    '#2E86AB',
    '#E84855',
    '#F4A261',
    '#2A9D8F',
    '#8338EC',
    '#FB8500',
    '#06D6A0',
];

const normalize = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const includesAny = (value, needles) => needles.some((needle) => value.includes(needle));

const parseMaybeNumber = (value) => {
    const n = parseFloat(value);
    return Number.isNaN(n) ? null : n;
};

const hmsToMinutes = (value) => {
    if (!value || typeof value !== 'string' || !value.includes(':')) return null;
    const parts = value.split(':').map((part) => parseInt(part, 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return null;
    const [hours, minutes, seconds] = parts;
    return Math.round((((hours * 3600) + (minutes * 60) + seconds) / 60) * 100) / 100;
};

const collectHours = (seriesList = []) => {
    const seen = new Set();
    const ordered = [];
    seriesList.forEach((series) => {
        (series.data || []).forEach((point) => {
            if (!seen.has(point.hour)) {
                seen.add(point.hour);
                ordered.push(point.hour);
            }
        });
    });
    return ordered.sort((a, b) => a.localeCompare(b));
};

const buildSeriesValues = (series, hours, valueParser) => {
    const byHour = new Map((series.data || []).map((point) => [point.hour, point.value]));
    return hours.map((hour) => valueParser(byHour.get(hour)));
};

const getLastParsedValue = (series, valueParser) => {
    const values = (series.data || [])
        .map((point) => valueParser(point.value))
        .filter((value) => value != null);
    return values.length > 0 ? values[values.length - 1] : 0;
};

const MultiSeriesChart = ({ title, subtitle, hours, seriesList, valueParser, chartType = 'line', formatterSuffix = '' }) => {
    const option = {
        color: seriesList.map((_, idx) => PALETTE[idx % PALETTE.length]),
        tooltip: {
            trigger: 'axis',
            formatter: (params) => {
                const lines = [`${params[0]?.axisValue || ''}`];
                params.forEach((param) => {
                    const rawValue = param.data == null ? '--' : param.data;
                    lines.push(`${param.marker} ${param.seriesName}: ${rawValue}${formatterSuffix}`);
                });
                return lines.join('<br/>');
            },
        },
        legend: {
            top: 0,
            type: 'scroll',
            textStyle: { fontSize: 11 },
        },
        grid: { top: 54, left: 55, right: 20, bottom: 50 },
        xAxis: {
            type: 'category',
            data: hours,
            name: 'Hour',
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: { fontSize: 11, rotate: hours.length > 12 ? 35 : 0 },
        },
        yAxis: {
            type: 'value',
            min: 0,
            scale: false,
        },
        series: seriesList.map((series, idx) => ({
            name: series.display_name,
            type: chartType,
            data: buildSeriesValues(series, hours, valueParser),
            smooth: chartType === 'line',
            showSymbol: chartType === 'line',
            barMaxWidth: chartType === 'bar' ? 28 : undefined,
            areaStyle: chartType === 'line' ? { opacity: 0.06 } : undefined,
            lineStyle: chartType === 'line' ? { width: 3 } : undefined,
            emphasis: { focus: 'series' },
            color: PALETTE[idx % PALETTE.length],
        })),
    };

    return (
        <Card padding="md" shadow="xs" radius="md">
            <Group position="apart" align="flex-start" mb="xs">
                <div>
                    <Text size="sm" weight={600} color="var(--color-bold-text)">
                        {title}
                    </Text>
                    {subtitle ? (
                        <Text size="xs" color="dimmed" mt={2}>
                            {subtitle}
                        </Text>
                    ) : null}
                </div>
            </Group>
            <ReactECharts option={option} style={{ height: 280 }} />
        </Card>
    );
};

const StackedUtilizationChart = ({ title, subtitle, seriesList }) => {
    const bars = seriesList.map((series, idx) => ({
        name: series.display_name,
        value: getLastParsedValue(series, hmsToMinutes),
        color: PALETTE[idx % PALETTE.length],
    }));

    const option = {
        color: bars.map((item) => item.color),
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const total = params.reduce((sum, item) => sum + (item.value || 0), 0);
                const lines = [`Selected Day`, `Total: ${total} min`];
                params.forEach((item) => {
                    const pct = total > 0 ? ` (${((item.value / total) * 100).toFixed(1)}%)` : '';
                    lines.push(`${item.marker} ${item.seriesName}: ${item.value || 0} min${pct}`);
                });
                return lines.join('<br/>');
            },
        },
        legend: {
            top: 0,
            type: 'scroll',
            textStyle: { fontSize: 11 },
        },
        grid: { top: 54, left: 24, right: 24, bottom: 24, containLabel: true },
        xAxis: {
            type: 'value',
            min: 0,
            name: 'Minutes',
            nameLocation: 'middle',
            nameGap: 28,
        },
        yAxis: {
            type: 'category',
            data: ['Selected Day'],
        },
        series: bars.map((item) => ({
            name: item.name,
            type: 'bar',
            stack: 'utilization',
            data: [item.value],
            barWidth: 42,
            label: {
                show: item.value > 0,
                position: 'inside',
                formatter: ({ value }) => `${value} min`,
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
            },
            emphasis: { focus: 'series' },
            color: item.color,
        })),
    };

    return (
        <Card padding="md" shadow="xs" radius="md">
            <Group position="apart" align="flex-start" mb="xs">
                <div>
                    <Text size="sm" weight={600} color="var(--color-bold-text)">
                        {title}
                    </Text>
                    {subtitle ? (
                        <Text size="xs" color="dimmed" mt={2}>
                            {subtitle}
                        </Text>
                    ) : null}
                </div>
            </Group>
            <ReactECharts option={option} style={{ height: 220 }} />
        </Card>
    );
};

const SingleSeriesChart = ({ chart, index }) => {
    const { display_name, chart_type, data } = chart;
    const color = PALETTE[index % PALETTE.length];

    const hours = data.map((d) => d.hour);
    const values = data.map((d) => {
        const n = parseFloat(d.value);
        return Number.isNaN(n) ? null : n;
    });

    const option = {
        color: [color],
        tooltip: {
            trigger: 'axis',
            formatter: (params) => {
                const p = params[0];
                return `${p.axisValue}<br/>${display_name}: ${data[p.dataIndex]?.value ?? p.value}`;
            },
        },
        grid: { top: 36, left: 55, right: 20, bottom: 50 },
        xAxis: {
            type: 'category',
            data: hours,
            name: 'Hour',
            nameLocation: 'middle',
            nameGap: 32,
            axisLabel: { fontSize: 11, rotate: hours.length > 12 ? 35 : 0 },
        },
        yAxis: {
            type: 'value',
            min: 0,
            scale: false,
        },
        series: [{
            name: display_name,
            type: chart_type,
            data: values,
            smooth: chart_type === 'line',
            showSymbol: chart_type === 'line' && data.length <= 24,
            barMaxWidth: 32,
            areaStyle: chart_type === 'line' ? { color, opacity: 0.08 } : undefined,
            lineStyle: chart_type === 'line' ? { width: 3 } : undefined,
            color,
        }],
    };

    return (
        <Card padding="md" shadow="xs" radius="md">
            <Text size="sm" weight={600} color="var(--color-bold-text)" mb="xs">
                {display_name}
            </Text>
            <ReactECharts option={option} style={{ height: 240 }} />
        </Card>
    );
};

const KpiMachines = ({ machineid }) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const [charts, setCharts] = useState([]);
    const [date, setDate] = useState(todayStr);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!machineid) return;

        setLoading(true);
        setError('');
        setCharts([]);

        client
            .get('/api/kpi/', { params: { machine_id: machineid, date } })
            .then((res) => {
                setCharts(res.data.charts || []);
            })
            .catch((err) => {
                console.error('[KpiMachines] fetch error:', err);
                setError('Could not load KPI data.');
            })
            .finally(() => setLoading(false));
    }, [machineid, date]);

    const groupedCharts = useMemo(() => {
        const environmental = [];
        const timeCharts = [];
        const singles = [];

        charts.forEach((chart) => {
            const key = normalize(chart.internal_name || chart.display_name);

            if (includesAny(key, ['waterlevel', 'tanklevel'])) {
                return;
            }

            if (includesAny(key, ['temperature', 'humidity', 'dewpoint'])) {
                environmental.push(chart);
            } else if (includesAny(key, ['runtime', 'idletime'])) {
                timeCharts.push(chart);
            } else {
                singles.push(chart);
            }
        });

        const result = [];

        if (environmental.length > 0) {
            result.push({
                key: 'environmental',
                layout: 'full',
                node: (
                    <MultiSeriesChart
                        title="Environmental Conditions"
                        subtitle="Temperature, Humidity, and Dew Point"
                        hours={collectHours(environmental)}
                        seriesList={environmental}
                        valueParser={parseMaybeNumber}
                        chartType="line"
                    />
                ),
            });
        }

        if (timeCharts.length > 0) {
            result.push({
                key: 'runtime-idle',
                layout: 'full',
                node: (
                    <StackedUtilizationChart
                        title="Runtime vs Idle Time"
                        subtitle="Daily utilization split for the selected date"
                        seriesList={timeCharts}
                    />
                ),
            });
        }

        singles.forEach((chart, idx) => {
            result.push({
                key: chart.internal_name,
                layout: 'grid',
                node: <SingleSeriesChart chart={chart} index={idx} />,
            });
        });

        return result;
    }, [charts]);

    if (loading) return <LoaderComponent />;

    if (error) {
        return <Text color="red" p="md">{error}</Text>;
    }

    if (charts.length === 0) {
        return <NoDataAvailable />;
    }

    const fullWidthCharts = groupedCharts.filter((item) => item.layout === 'full');
    const gridCharts = groupedCharts.filter((item) => item.layout === 'grid');
    const cols = gridCharts.length === 1 ? 1 : gridCharts.length === 2 ? 2 : 3;

    return (
        <Container fluid p={0}>
            <Group position="apart" mb="md" px={4} align="flex-end">
                <div>
                    <Title order={6} color="var(--color-text)" fw={500}>
                        Hourly Data - {date}
                    </Title>
                    <Text size="xs" color="dimmed" mt={2}>
                        Last record per hour from machine log
                    </Text>
                </div>
                <TextInput
                    type="date"
                    label="Select date"
                    value={date}
                    max={todayStr}
                    onChange={(event) => setDate(event.currentTarget.value || todayStr)}
                    styles={{ input: { minWidth: 180 } }}
                />
            </Group>

            {fullWidthCharts.map((item) => (
                <div key={item.key} style={{ marginBottom: 16 }}>
                    {item.node}
                </div>
            ))}

            {gridCharts.length > 0 ? (
                <SimpleGrid
                    cols={cols}
                    spacing="md"
                    breakpoints={[
                        { maxWidth: 'md', cols: 2 },
                        { maxWidth: 'sm', cols: 1 },
                    ]}
                >
                    {gridCharts.map((item) => (
                        <div key={item.key}>{item.node}</div>
                    ))}
                </SimpleGrid>
            ) : null}
        </Container>
    );
};

export default KpiMachines;
